import { Signal } from "../core/Signal.js";
import { DebugViewWindow, DebugViewWindowOptions } from "./DebugViewWindow.js";
import { object } from "./ObjectUtil.js";

export interface DebugViewItemOptions<Data extends Object = any> {
    uid: string;
    tooltip: string;
    icon?: string;
    iconActive?: string;
    iconDisabled?: string;
    active?: boolean;
    cookie?: boolean;
    reloadAfterToggle?: boolean;
    localStorage?: boolean;
    url?: string;
    urlTarget?: string;
    interactive?: boolean;
    label?: string;
    html?: string;
    toggle?: boolean;
    visible?: boolean;
    disabled?: boolean;
    windows?: DebugViewWindowOptions[];
    onInit?: (item: DebugViewItem) => void;
    onClick?: (item: DebugViewItem) => void;
    onClickDisabled?: (item: DebugViewItem) => void;
    onToggle?: (item: DebugViewItem) => void;
    onToggleInit?: (item: DebugViewItem) => void;
}

export class DebugViewItem {

    public readonly onToggle = Signal.create<[DebugViewItem]>();
    public readonly onClick = Signal.create<[DebugViewItem]>();
    public readonly onClickDisabled = Signal.create<[DebugViewItem]>();

    public readonly element: HTMLElement;
    public readonly uid: string;

    private _bindedWindows: DebugViewWindow[] = [];

    private _cookie: boolean = false;
    private _localStorage: boolean = false;
    private _toggle: boolean = false;
    private _tooltip: string = "";
    private _icon: string = "";
    private _iconActive: string = "";
    private _iconDisabled: string = "";
    private _url: string = "";
    private _urlTarget: string = "";

    constructor(options: DebugViewItemOptions) {

        let toggle = (
            options.toggle === true
            || typeof options.onToggle === "function"
            || typeof options.onToggleInit === "function"
            || options.cookie
            || (options.windows && options.windows.length > 0)
        );

        options = object.merge({
            active: false,
            icon: "",
            iconActive: "",
            iconDisabled: "",
            interactive: true,
            cookie: false,
            reloadAfterToggle: options.cookie,
            localStorage: toggle,
            tooltip: "",
            url: "",
            visible: true,
            disabled: false,
            toggle: toggle,
            urlTarget: "_self"
        }, options);

        this.uid = options.uid;

        this._urlTarget = options.urlTarget;

        this.element = document.createElement("div");
        this.element.classList.add("e5-debug-view-item");
        this.element.innerHTML = `
        <div class="e5-debug-view-item__icon e5-hidden"></div>
        <div class="e5-debug-view-item__label e5-hidden"></div>
        <div class="e5-debug-view-item__html e5-hidden"></div>
        `;

        this.element.addEventListener("click", () => {
            if (this.disabled) {
                this.onClickDisabled.dispatch(this);
                return;
            }

            if (this.toggle) {
                this.active = !this.active;
            }
            
            this.onClick.dispatch(this);

            if (this.toggle
                && options.reloadAfterToggle) {
                window.location.reload();
                return;
            }
        });

        this.onToggle.add(() => {
            this.store();
        });

        if (options.cookie) {
            if (Cookies.get(options.uid)) {
                options.active = true;
            } else {
                options.active = false;
            }
        }

        if (options.localStorage) {
            let v = 'off';
            try {
                v = localStorage.getItem(options.uid);
            } catch (error) {
            }
            if (v === "on") {
                options.active = true;
            } else {
                options.active = false;
            }
        }

        if (typeof options.onClick === "function") {
            this.onClick.add(options.onClick);
        }

        if (typeof options.onClickDisabled === "function") {
            this.onClickDisabled.add(options.onClickDisabled);
        }

        if (typeof options.onToggle === "function") {
            this.onToggle.add(options.onToggle);
        }

        if (typeof options.onToggleInit === "function") {
            this.onToggle.add(options.onToggleInit);
        }

        this.tooltip = options.tooltip;
        this.active = options.active;
        this.label = options.label;
        this.html = options.html;
        this.interactive = options.interactive;
        this.visible = options.visible;
        this.toggle = options.toggle;
        this.disabled = options.disabled;
        this.localStorage = options.localStorage;
        this.cookie = options.cookie;
        this.url = options.url;
        this._icon = options.icon;
        this._iconActive = options.iconActive;
        this._iconDisabled = options.iconDisabled;
        this.refreshIcon();

        if (typeof options.onToggleInit === "function") {
            options.onToggleInit(this);
        }

        if (typeof options.onInit === "function") {
            options.onInit(this);
        }
    }

    private refreshIcon(): void {
        let icon = "";
        if (this.disabled && this.iconDisabled) {
            icon = this.iconDisabled;
        } else if (this.active && this.iconActive) {
            icon = this.iconActive;
        } else if (this.icon) {
            icon = this.icon;
        }

        const e = this.element.querySelector(".e5-debug-view-item__icon");
        e.className = "e5-debug-view-item__icon " + icon;
        e.classList.toggle("e5-hidden", !icon);
    }

    public bindWindow(window: DebugViewWindow): void {
        this.unbindWindow(window);
        this._bindedWindows.push(window);
        window.visible = this.active;
    }

    public unbindWindow(window: DebugViewWindow): void {
        const index = this._bindedWindows.indexOf(window);
        if (index === -1) {
            return;
        }
        this._bindedWindows.splice(index, 1);
    }

    public get url(): string {
        return this._url;
    }

    public set url(value: string) {
        if (this._url == value) {
            return;
        }
        this._url = value;

        let e: HTMLAnchorElement = this.element.querySelector(".e5-debug-view-item__url");
        if (value) {
            if (!e) {
                e = document.createElement("a");
                e.target = this._urlTarget;
                e.className = "e5-debug-view-item__url";
                this.element.append(e);
            }
            e.href = value;

        } else {
            if (e) {
                e.remove();
            }
        }
    }

    public get icon(): string {
        return this._icon;
    }

    public set icon(value: string) {
        if (this._icon == value) {
            return;
        }
        this._icon = value;
        this.refreshIcon();
    }

    public get iconActive(): string {
        return this._iconActive;
    }

    public set iconActive(value: string) {
        if (this._iconActive == value) {
            return;
        }
        this._iconActive = value;
        this.refreshIcon();
    }

    public get iconDisabled(): string {
        return this._iconDisabled;
    }

    public set iconDisabled(value: string) {
        if (this._iconDisabled == value) {
            return;
        }
        this._iconDisabled = value;
        this.refreshIcon();
    }

    private store(): void {
        if (this._cookie && this.active) {
            Cookies.set(this.uid, "on");
        } else {
            Cookies.remove(this.uid);
        }

        try {
            if (this._localStorage && this.active) {
                localStorage.setItem(this.uid, "on");
            } else {
                localStorage.removeItem(this.uid);
            }
        } catch (error) {
        }
    }

    public get tooltip(): string {
        return this._tooltip;
    }

    public set tooltip(value: string) {
        if (this._tooltip == value) {
            return;
        }
        this._tooltip = value;
        this.element.setAttribute("data-debug-tooltip", value);
    }

    public get toggle(): boolean {
        return this._toggle;
    }

    public set toggle(value: boolean) {
        this._toggle = value;
    }

    public get cookie(): boolean {
        return this._cookie;
    }

    public set cookie(value: boolean) {
        if (this._cookie == value) {
            return;
        }
        this._cookie = value;
        this.store();
    }

    public get localStorage(): boolean {
        return this._localStorage;
    }

    public set localStorage(value: boolean) {
        if (this._localStorage == value) {
            return;
        }
        this._localStorage = value;
        this.store();
    }

    public get disabled(): boolean {
        return this.element.classList.contains("e5-debug-view-item--disabled");
    }

    public set disabled(value: boolean) {
        if (this.disabled === value) {
            return;
        }
        this.element.classList.toggle("e5-debug-view-item--disabled", value);
        this.refreshIcon();
    }

    public get interactive(): boolean {
        return this.element.classList.contains("e5-debug-view-item--interactive");
    }

    public set interactive(value: boolean) {
        if (this.interactive === value) {
            return;
        }
        this.element.classList.toggle("e5-debug-view-item--interactive", value);
    }

    public get active(): boolean {
        return this.element.classList.contains("e5-debug-view-item--active");
    }

    public set active(value: boolean) {
        if (this.active === value) {
            return;
        }
        this.element.classList.toggle("e5-debug-view-item--active", value);
        this.refreshIcon();

        for (let w of this._bindedWindows) {
            w.visible = value;
        }

        this.onToggle.dispatch(this);
    }

    public get visible(): boolean {
        return !this.element.classList.contains("e5-debug-view-item--hidden");
    }

    public set visible(value: boolean) {
        if (this.visible === value) {
            return;
        }
        this.element.classList.toggle("e5-debug-view-item--hidden", !value);
    }

    public get label(): string {
        return this.element.querySelector(".e5-debug-view-item__label")
            .textContent;
    }

    public set label(value: string) {
        if (this.label == value) {
            return;
        }
        const label = this.element.querySelector(".e5-debug-view-item__label");
        label.textContent = value || "";
        label.classList.toggle("e5-hidden", !value);
    }

    public get html(): string {
        return this.element.querySelector(".e5-debug-view-item__html")
            .textContent;
    }

    public set html(value: string) {
        if (this.html == value) {
            return;
        }
        const html = this.element.querySelector(".e5-debug-view-item__html");
        html.innerHTML = value || "";
        html.classList.toggle("e5-hidden", !value);
    }
}